import React, {forwardRef, useContext, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState} from "react";
import OwlCarousel from "react-owl-carousel";
import classnames from "classnames";
import {createUUId} from "../../../../../../../core/services/utils/utils";
import {UserHomepageProductContext, UserHomepageViewContext} from "../../../../index";
import UserWebHomepageProductCard from "../product-card";
import Sizes from "../../../../../../../core/models/static/sizes";
import {ReactComponent as Prev} from "../../../../../../../assets/images/web-homepage/slide-show-prev.svg";
import {ReactComponent as Next} from "../../../../../../../assets/images/web-homepage/slide-show-next.svg";

const UserWebHomepageProductSlider = forwardRef(({
                                                     products,
                                                     type,
                                                     onSelectionChanged,
                                                     onProductClicked,
                                                     onTagClicked,
                                                     carouselSlideSpeed = 250,
                                                 }, ref) => {
    const [state, setState] = useState({
        hasNext: false,
        hasPrev: false,
        currentIndex: 0
    })
    const {viewportWidth} = useContext(UserHomepageViewContext);
    const [childHeight, setChildHeight] = useState(0);
    const initialRender = useRef(true);
    /**@type {React.MutableRefObject<import('react-owl-carousel/umd/OwlCarousel.d.ts').default>}*/
    const carouselRef = useRef();
    const containerIdRef = useRef(createUUId(true));
    const [forceUpdate, setForceUpdate] = useState(false);
    const prevProductsLength = useRef(-1);
    const setSettingsRef = useRef(false);

    const responsive = useMemo(() => {
        switch (viewportWidth) {
            case "auto":
            case 'lg':
                return {[Sizes.xs]: {items: 5}};
            case 'xxl':
                return {[Sizes.xs]: {items: 7}};
            case 'xl':
                return {[Sizes.xs]: {items: 6}};
            case 'md':
                return {[Sizes.xs]: {items: 4}};
            case 'sm':
                return {[Sizes.xs]: {items: 2}};
            case 'xs':
                return {[Sizes.xs]: {items: 1}};
            default:
                return {[Sizes.xs]: {items: 5}};
        }
    }, [viewportWidth])


    /**
     * Exposes the carousel api ref functionalities to the parent component.
     */
    useImperativeHandle(ref, () => carouselRef.current, [carouselRef])

    /**
     * Before each change in the products' length:
     * - sets the prevProductsLength ref value with the previous length of the products.
     */
    useLayoutEffect(() => {
        const length = products?.length ?? 0;
        setSettingsRef.current = false;
        return () => {
            prevProductsLength.current = length;
        }
    }, [products?.length])

    /**
     * With each change in the products:
     * - re-renders of the component (specifically the carousel component) to ensure the updated products are shown
     * in the ui
     */
    useEffect(() => {
        setForceUpdate(prevState => !prevState);
    }, [products])


    /**
     * For Each change in view port width and containerIdRef:
     * - Sets a timer for getting all the elements in the carousel
     * - Calculates the max height of the elements in the list and sets the value in the context
     * - Increases the max height only in init render to account for box shadow rendering
     */
    useEffect(() => {
        if (!products?.length || !initialRender.current)
            return;
        initialRender.current = false;
        let initialHeightUpdate = true;
        const timer = setTimeout(() => {
            const containerElement = document.getElementById(containerIdRef.current);

            if (!containerElement)
                return;

            const products = containerElement.getElementsByClassName('product-container');
            if (!products?.length)
                return;

            let height = [...products]?.reduce((p, c) => Math.max(p, c.getBoundingClientRect().height), 0);

            if (initialHeightUpdate) {
                height += 10;
                initialHeightUpdate = false;
            }
            setChildHeight(height);

        }, 500)
        return () => clearTimeout(timer);
    }, [products, containerIdRef, viewportWidth])

    /**
     * With every change in view port size:
     * - navigates the carousel to the current index
     */
    useLayoutEffect(() => {
        try {
            carouselRef.current.to(state.currentIndex, carouselSlideSpeed);
        } catch (e) {
        }
    }, [viewportWidth, childHeight]);

    /**
     * Changes the carousel item based on the offset:
     *
     * * If the offset is greater than zero shifts it the right
     *  Otherwise, shifts it to the left
     *
     * @param {number} offset
     */
    const changeSlide = (offset) => {
        if (offset > 0) {
            return carouselRef.current.next(carouselSlideSpeed);
        }
        carouselRef.current.prev(carouselSlideSpeed);
    }

    /**
     * Changes the values of [hasNext] and [hasPrev] depending on the current state of the owl
     * carousel.
     * @param {Event} e
     */
    const onOwlCarouselChanged = (e) => {
        if (e?.item?.index === null)
            return;

        const pageInfo = e?.page ?? {};
        const itemsInfo = e?.item ?? {};


        if (e?.property?.name !== 'position') {
            if (itemsInfo?.count !== products?.length)
                return

            return setState(prevState => {
                let newIndex = prevState.currentIndex;

                // removed a product
                if (prevProductsLength.current > products.length) {
                    if (Math.abs((prevState?.currentIndex + 1) - products.length) <= 2) {
                        newIndex = Math.max(products.length - pageInfo.size, 0);
                        for (let i = 0; i < newIndex; i++) {
                            carouselRef.current?.next(0);
                        }
                    }
                }

                // added a new product
                if (prevProductsLength.current < products.length) {
                    newIndex = Math.max(products.length - pageInfo.size, 0);
                    for (let i = 0; i < newIndex; i++) {
                        carouselRef.current?.next(0);
                    }
                }

                const hasNext = (newIndex + pageInfo.size) < itemsInfo.count;
                const hasPrev = newIndex > 0;
                return {
                    hasNext: hasNext,
                    hasPrev: hasPrev,
                    currentIndex: newIndex,
                }
            })

        }

        const hasNext = (itemsInfo.index + pageInfo.size) < itemsInfo.count;
        const hasPrev = itemsInfo.index > 0;

        setState({
            hasNext: hasNext,
            hasPrev: hasPrev,
            currentIndex: itemsInfo.index,
        })
        if (onSelectionChanged && state.currentIndex !== itemsInfo.index && typeof (itemsInfo.index) === "number") {
            onSelectionChanged(products[itemsInfo.index], itemsInfo.index);
        }
    }


    const owlCarousel = useMemo(() => (
        <OwlCarousel
            autoplay={false}
            loop={false}
            margin={0}
            responsive={responsive}
            ref={carouselRef}
            onChanged={onOwlCarouselChanged}
            id={containerIdRef.current}
            className={'owl-products'}>
            {
                products?.map((item) => (
                    <div key={item?.id} className={classnames('user-web-homepage-product-slider__slide')}>
                        <UserWebHomepageProductCard
                            isChildOfCarousel
                            data={item}
                            type={type}
                            onProductClicked={onProductClicked}
                            onTagClicked={onTagClicked}
                        />
                    </div>
                ))
            }
        </OwlCarousel>
    ), [responsive, viewportWidth, childHeight, products, type, forceUpdate])

    return (
        <>
            <UserHomepageProductContext.Provider value={{enforcedHeight: 'auto'}}>
                <div className={'user-web-homepage-product-slider'}>
                    {(state.hasPrev || state.hasNext) &&
                        <button
                            disabled={!state.hasPrev}
                            className={'slider-icon-button prev'}
                            onClick={() => changeSlide(-1)}>
                            <Prev/>
                        </button>
                    }
                    {owlCarousel}
                    {(state.hasPrev || state.hasNext) &&
                        <button
                            disabled={!state.hasNext}
                            className={'slider-icon-button next'}
                            onClick={() => changeSlide(1)}>
                            <Next/>
                        </button>
                    }
                </div>
            </UserHomepageProductContext.Provider>
        </>
    )
})

export default UserWebHomepageProductSlider;
